import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { Badge } from "@/components/Badge";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Loader2, DatabaseBackup, Download, RotateCcw, Trash2, ShieldCheck, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const KIND = {
  auto: { label: "Automatic", color: "blue" },
  manual: { label: "Manual", color: "green" },
  pre_restore: { label: "Safety (pre-restore)", color: "orange" },
};

function fmtDT(iso) {
  try { return new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}

export default function Backups() {
  const qc = useQueryClient();
  const [restoreTarget, setRestoreTarget] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);

  const { data: backups = [], isLoading } = useQuery({ queryKey: ["backups"], queryFn: async () => (await api.get("/backups")).data });

  const makeBackup = useMutation({
    mutationFn: async () => (await api.post("/backups")).data,
    onSuccess: () => { toast.success("Backup created"); qc.invalidateQueries({ queryKey: ["backups"] }); },
    onError: () => toast.error("Could not create backup"),
  });

  const restore = useMutation({
    mutationFn: async (id) => (await api.post(`/backups/${id}/restore`)).data,
    onSuccess: (d) => { toast.success(`Data restored to ${fmtDT(d.restored_from)}`); setRestoreTarget(null); qc.invalidateQueries({ queryKey: ["backups"] }); setTimeout(() => window.location.reload(), 900); },
    onError: () => { toast.error("Restore failed"); setRestoreTarget(null); },
  });

  const del = useMutation({
    mutationFn: async (id) => (await api.delete(`/backups/${id}`)).data,
    onSuccess: () => { toast.success("Backup deleted"); qc.invalidateQueries({ queryKey: ["backups"] }); },
  });

  const download = async (b) => {
    setDownloadingId(b.id);
    try {
      const res = await api.get(`/backups/${b.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `classic24-backup-${b.created_at.slice(0, 19).replace(/:/g, "-")}.json`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
    finally { setDownloadingId(null); }
  };

  return (
    <div className="space-y-6" data-testid="backups-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 font-heading text-2xl font-bold tracking-tight sm:text-3xl">
            <DatabaseBackup className="h-6 w-6 text-blue-600" />Backups
          </h1>
          <p className="mt-1 text-sm text-zinc-500">Daily automatic snapshots at 2:00 AM IST + manual backups. Restore or download any snapshot. Last 7 days are kept.</p>
        </div>
        <Button onClick={() => makeBackup.mutate()} disabled={makeBackup.isPending} data-testid="backup-now-button">
          {makeBackup.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ShieldCheck className="mr-2 h-4 w-4" />}Backup Now
        </Button>
      </div>

      {isLoading ? (
        <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-zinc-400" /></div>
      ) : backups.length === 0 ? (
        <div className="rounded-lg border border-dashed border-zinc-200 py-16 text-center text-sm text-zinc-400">No backups yet. Click "Backup Now" to create your first snapshot.</div>
      ) : (
        <div className="space-y-2">
          {backups.map((b) => (
            <Card key={b.id} className="flex flex-wrap items-center justify-between gap-3 rounded-lg border-zinc-200 p-4 shadow-sm" data-testid={`backup-${b.id}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-heading font-semibold text-zinc-900">{fmtDT(b.created_at)}</span>
                  <Badge color={KIND[b.kind]?.color || "grey"}>{KIND[b.kind]?.label || b.kind}</Badge>
                </div>
                <div className="mt-0.5 text-xs text-zinc-500">
                  {b.total_records} records · {b.counts?.clients || 0} clients · {b.counts?.content_items || 0} content · {b.counts?.payments || 0} payments · {b.counts?.users || 0} team
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => download(b)} disabled={downloadingId === b.id} data-testid={`download-${b.id}`}>
                  {downloadingId === b.id ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Download className="mr-1.5 h-3.5 w-3.5" />}Download
                </Button>
                <Button variant="outline" size="sm" onClick={() => setRestoreTarget(b)} data-testid={`restore-${b.id}`}>
                  <RotateCcw className="mr-1.5 h-3.5 w-3.5" />Restore
                </Button>
                <Button variant="ghost" size="icon" onClick={() => del.mutate(b.id)} title="Delete" data-testid={`delete-${b.id}`}>
                  <Trash2 className="h-4 w-4 text-zinc-400 hover:text-red-500" />
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!restoreTarget} onOpenChange={(o) => !o && setRestoreTarget(null)}>
        <DialogContent data-testid="restore-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-heading"><AlertTriangle className="h-5 w-5 text-amber-500" />Restore this backup?</DialogTitle>
            <DialogDescription>Roll all data back to this saved snapshot. A safety backup is taken first.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm text-zinc-600">
            <p>This will <strong>replace all current data</strong> (clients, content, payments, team, etc.) with the snapshot from <strong>{restoreTarget && fmtDT(restoreTarget.created_at)}</strong>.</p>
            <p className="rounded-md border border-blue-100 bg-blue-50 px-3 py-2 text-xs text-blue-800">A safety backup of your current data is taken automatically first, so you can undo this.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRestoreTarget(null)} data-testid="restore-cancel">Cancel</Button>
            <Button onClick={() => restore.mutate(restoreTarget.id)} disabled={restore.isPending} data-testid="restore-confirm">
              {restore.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RotateCcw className="mr-2 h-4 w-4" />}Restore Data
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
